import React, { useState } from 'react';

import {
  ACCOUNT_ACCESS_LEVELS,
  ACCOUNT_ROLES,
  ACCOUNT_ROLE_PERMISSIONS,
  accountCount,
} from './accountModel';
import { ACCOUNT_SPRINT } from '@/lib/accountPaths';
import { useAccountRecord } from './AccountRecordContext';
import { AccountPageShell } from './AccountPageShell';
import { AccountDialog } from './AccountOverlays';
import {
  AccountCard,
  Chip,
  Grid,
  Muted,
  NotYetButton,
  Row,
  ScopeChip,
  SelectField,
  SmallButton,
  StatusChip,
  Table,
} from './primitives';
import type { AccountRole } from './accountFixture';

/**
 * AR-380 — Team + Access.
 *
 * v15 changed three things here. The roster and the roles land in sprint 1;
 * Data access and Team defaults come at sprint 2 (D-ACCT-33). The row states
 * the member and nothing more — Manage opens a pop-up carrying the role, Save,
 * Cancel and Deactivate, and Deactivate asks a second time (D-ACCT-46). And not
 * every plan allows an associate advisor, so the invite button is shown but
 * prompts an upgrade rather than pretending the seat exists.
 */

/** Data access and Team defaults arrive at sprint 2 (D-ACCT-33). */
const TEAM_SPRINT_2 = ACCOUNT_SPRINT >= 2;
/** Whether this member's plan carries an associate seat. */
const PLAN_ALLOWS_ASSOCIATES = false;

export const AccountTeam: React.FC = () => {
  const { record, members, setMemberRole, setConfirm, setMemberStatus } =
    useAccountRecord();
  const t = record.teamset;
  const [managing, setManaging] = useState<number | null>(null);
  const [pendingRole, setPendingRole] = useState<AccountRole | null>(null);
  const [roleOpen, setRoleOpen] = useState<string | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const managed = managing === null ? null : members.find((m) => m.index === managing);

  const rows = members.map((m) => [
    <>
      <b className="whitespace-nowrap text-gray-900 dark:text-gray-100">{m.name}</b>
      {m.you ? (
        <span className="ml-2">
          <Chip>you</Chip>
        </span>
      ) : null}
    </>,
    m.email,
    m.role,
    <StatusChip status={m.status} />,
    m.brand,
    m.seen,
    m.you ? (
      <Muted>—</Muted>
    ) : (
      <button
        type="button"
        onClick={() => {
          setManaging(m.index);
          setPendingRole(m.role);
        }}
        className="-my-1 inline-flex min-h-[24px] items-center py-1 text-[14px] text-orange-700 hover:underline dark:text-orange-400"
      >
        Manage
      </button>
    ),
  ]);

  return (
    <AccountPageShell page="team">
      <div className="rounded-lg border border-gray-200 bg-white px-4 py-3.5 dark:border-gray-700 dark:bg-[#1A2021]">
        <div className="mb-3 flex items-baseline justify-between gap-3">
          <span className="flex items-baseline gap-2.5">
            <span className="text-[13px] font-semibold uppercase tracking-[0.14em] text-orange-700 dark:text-orange-400">
              Team members
            </span>
            <small className="text-[12.5px] text-gray-500 dark:text-gray-400">
              {accountCount(members.length, 'member', 'members')}
            </small>
          </span>
          <SmallButton
            onClick={() =>
              PLAN_ALLOWS_ASSOCIATES ? undefined : setUpgradeOpen(true)
            }
          >
            Invite team member
          </SmallButton>
        </div>
        <Table
          columns={['Name', 'Email', 'Role', 'Status', 'Brand', 'Last active', '']}
          rows={rows}
          minWidth={880}
        />
      </div>

      {TEAM_SPRINT_2 ? (
        <Grid cols={2} className="mt-3.5">
          <AccountCard
            name="Data access"
            chip={<ScopeChip scope="owner" />}
            lead="Other products show these settings and link back here. This is the only place they change."
          >
            <Row
              label="Team CRM access"
              value={t.crm}
              extra="Arrives with the CRM."
              chip={<StatusChip status="Not yet" />}
            />
            <Row
              label="Card Vault sharing"
              value={t.vault}
              extra="Arrives with the CRM."
              chip={<StatusChip status="Not yet" />}
              last
            />
          </AccountCard>

          <AccountCard
            cardKey="teamdef"
            name="Team defaults"
            chip={<ScopeChip scope="owner" />}
            form={
              <>
                <SelectField
                  path="teamset.brand"
                  label="Default brand"
                  options={[
                    "Each advisor's own brand",
                    `${record.biz.brand} for everyone`,
                  ]}
                />
                <SelectField
                  path="teamset.terms"
                  label="Default proposal terms"
                  options={[
                    "Each advisor's own terms",
                    `${record.biz.brand} terms on every proposal`,
                  ]}
                />
              </>
            }
          >
            <Row label="Default brand" value={t.brand} />
            <Row label="Default proposal terms" value={t.terms} last />
          </AccountCard>
        </Grid>
      ) : null}

      <Grid cols={2} className="mt-3.5">
        <AccountCard name="Roles + permissions">
          {ACCOUNT_ROLE_PERMISSIONS.map(([role, scope, list], i) => {
            const open = roleOpen === role;
            return (
              <div
                key={role}
                className={
                  i === ACCOUNT_ROLE_PERMISSIONS.length - 1
                    ? 'py-2.5'
                    : 'border-b border-gray-100 py-2.5 dark:border-gray-800'
                }
              >
                <button
                  type="button"
                  aria-expanded={open}
                  onClick={() => setRoleOpen(open ? null : role)}
                  className="flex items-baseline gap-1.5 text-gray-900 dark:text-gray-100"
                >
                  <span className="inline-block w-3 text-center text-gray-400">
                    {open ? '▾' : '▸'}
                  </span>
                  <b className="font-semibold">{role}</b>
                </button>
                <small className="block text-[14px] text-gray-500 dark:text-gray-400">
                  {scope}
                </small>
                {open ? (
                  <ul className="ml-5 mt-1 list-disc text-[14.5px] text-gray-800 dark:text-gray-200">
                    {list.map((x) => (
                      <li key={x}>{x}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            );
          })}
        </AccountCard>
        <div />
      </Grid>

      {/* Manage is a pop-up now: the role, Save, Cancel and Deactivate in one
          place, instead of a select that was invisible until pressed and a
          Deactivate sitting beside Save in a table cell (D-ACCT-46). */}
      {managed ? (
        <AccountDialog
          title={`Manage ${managed.name}`}
          sub={managed.email}
          onClose={() => {
            setManaging(null);
            setPendingRole(null);
          }}
          actions={
            <>
              <SmallButton
                primary
                onClick={() => {
                  if (pendingRole) setMemberRole(managed.index, pendingRole);
                  setManaging(null);
                  setPendingRole(null);
                }}
              >
                Save
              </SmallButton>
              <SmallButton
                onClick={() => {
                  setManaging(null);
                  setPendingRole(null);
                }}
              >
                Cancel
              </SmallButton>
              {managed.status === 'Deactivated' ? (
                <SmallButton
                  onClick={() => {
                    setMemberStatus(managed.index, 'Active');
                    setManaging(null);
                  }}
                >
                  Reactivate
                </SmallButton>
              ) : (
                /* Deactivate asks a second time — the confirmation says what
                   they lose and what stays. */
                <SmallButton
                  onClick={() => setConfirm({ kind: 'deactivate', index: managed.index })}
                >
                  Deactivate
                </SmallButton>
              )}
            </>
          }
        >
          <div className="mt-3">
            <label
              htmlFor="account-manage-role"
              className="mb-1 block text-[13px] font-semibold uppercase tracking-[0.1em] text-gray-500 dark:text-gray-400"
            >
              Role
            </label>
            <select
              id="account-manage-role"
              value={pendingRole ?? managed.role}
              onChange={(e) => setPendingRole(e.target.value as AccountRole)}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-[15px] text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
            >
              {ACCOUNT_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <Row label="Status" value={managed.status} last />
          </div>
        </AccountDialog>
      ) : null}

      {/* Not every plan carries an associate seat. The button is shown either
          way; on a plan without one it prompts the upgrade rather than
          pretending the seat exists. */}
      {upgradeOpen ? (
        <AccountDialog
          title="Add an associate advisor"
          onClose={() => setUpgradeOpen(false)}
          actions={
            <>
              <SmallButton primary onClick={() => setUpgradeOpen(false)}>
                Compare plans
              </SmallButton>
              <SmallButton onClick={() => setUpgradeOpen(false)}>Not now</SmallButton>
            </>
          }
        >
          <p className="mt-3 text-[14.5px] leading-relaxed text-gray-700 dark:text-gray-300">
            Your current plan does not include an associate advisor seat. Upgrading your
            membership adds one, and your associate keeps their own clients, trips and
            public profile under your agency.
          </p>
        </AccountDialog>
      ) : null}
    </AccountPageShell>
  );
};

export default AccountTeam;
